import React, { useEffect, useState } from 'react'

import ColumnLabel from 'components/headers/table/ColumnLabel'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEllipsisVertical, faPencil, faPlus, faTrash, faWarning } from '@fortawesome/free-solid-svg-icons'
import _ from 'lodash'
import InputField from 'components/inputs/InputField'
import { FormInputError } from 'IndexProductScreen'
import { SearchBar } from 'components/inputs/SearchBar'
import { PageLoadSpinner } from 'components/spinners/PageLoadSpinner'
import { BaseModal } from 'components/modals/BaseModal'
import { usePipeOpenings } from 'hooks/usePipeOpenings'
import { ConfirmActionModal } from 'components/modals/ConfirmActionModal'
import { usePipeMaterials } from 'hooks/usePipeMaterials'
import { toastError, toastSuccess } from 'services/Toast'


export type PipeOpeningConfig = {
    id: string,
    insideDiameter: number,
	material: string,
	outsideDiameter: number, 
	openingSize: number,
    isDeleted: boolean
}

export type AddPipeOpeningConfigRequest = PipeOpeningConfigFormState

type PipeOpeningConfigFormState = {
    insideDiameter: number,
	material: string,
	outsideDiameter: number, 
	openingSize: number
}

const initialFormState: PipeOpeningConfigFormState = {
    insideDiameter: 0,
	material: '',
	outsideDiameter: 0, 
	openingSize: 0
}

const initialFormInputErrorState: FormInputError = {
    isError: false,
    errorMessage: null
}

interface FormErrorState {
    insideDiameter: FormInputError,
    material: FormInputError,
    outsideDiameter: FormInputError,
    openingSize: FormInputError
}

const initialFormErrorState: FormErrorState = {
    insideDiameter: initialFormInputErrorState,
	material: initialFormInputErrorState,
	outsideDiameter: initialFormInputErrorState, 
	openingSize: initialFormInputErrorState
}

const ManagePipeOpeningsScreen = ({

}:{

}) => {
    const [itemToDelete, setItemToDelete] = useState<PipeOpeningConfig>(null)
    const [itemToEdit, setItemToEdit] = useState<PipeOpeningConfig>(null)
    const [isInEditState, setIsInEditState] = useState<boolean>(false)
    const [isConfirmDeleteModalActive, setIsConfirmDeleteModalActive] = useState<boolean>(false)
    const [isConfirmEditModalActive, setIsConfirmEditModalActive] = useState<boolean>(false)
    const [filterablePipeOpenings, setFilterablePipeOpenings] = useState<PipeOpeningConfig[]>(null)
    const [isPipeOpeningFormModalActive, setIsPipeOpeningFormModalActive] = useState<boolean>(false)
    const [pipeOpeningConfigFormState, setPipeOpeningConfigFormState] = useState<PipeOpeningConfigFormState>(initialFormState)
    const [formErrorState, setFormErrorState] = useState<FormErrorState>(initialFormErrorState)
    const { pipeMaterials } = usePipeMaterials()
    const { pipeOpenings, deletePipeOpening, isDeleting: isDeletingPipeOpening, addPipeOpening, isAdding: isAddingPipeOpening, editPipeOpening, isEditing: isEditingPipeOpening } = usePipeOpenings()

    // Update filterable pipe openings when pipeOpenings data changes
    useEffect(() => {
        if (pipeOpenings) {
            const nonDeletedPipeOpenings: PipeOpeningConfig[] = pipeOpenings.filter(_ => !_.isDeleted)
            setFilterablePipeOpenings(nonDeletedPipeOpenings)
        }
    }, [pipeOpenings])

    const onChangeSearchQuery = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (pipeOpenings) {
            const nonDeletedPipeOpenings = pipeOpenings.filter(_ => !_.isDeleted)
            setFilterablePipeOpenings(
                nonDeletedPipeOpenings.filter(item =>  item.material.toLowerCase().split("_").join(" ").startsWith(e.target.value.toLowerCase()))
            )
        }
    }
    
    const onClearSearchQuery = (e: React.MouseEvent) => {
        if (pipeOpenings) {
            const nonDeletedPipeOpenings: PipeOpeningConfig[] = 
                pipeOpenings
                    .filter(_ => !_.isDeleted)
            
            setFilterablePipeOpenings(nonDeletedPipeOpenings)
        }
    }

    const onCancelModal = (e: React.MouseEvent) => {
        e.preventDefault()
        setPipeOpeningConfigFormState(initialFormState)
        setFormErrorState(initialFormErrorState)
        setIsPipeOpeningFormModalActive(false)
        setIsInEditState(false)
        setItemToEdit(null)
    }

    const onCancelConfirmDeleteModal = () => {
        setIsConfirmDeleteModalActive(false)
        setItemToDelete(null)
    }

    const onDeletePipeOpeningItem = async (e: React.MouseEvent, itemToDelete: PipeOpeningConfig) => {
        e.preventDefault(); 
        setItemToDelete(itemToDelete)
        setIsConfirmDeleteModalActive(true) 
    }

    const onEditPipeOpeningItem = async (e: React.MouseEvent, itemToEdit: PipeOpeningConfig) => {
        e.preventDefault(); 

        setIsInEditState(true)
        setItemToEdit(itemToEdit)

        setPipeOpeningConfigFormState({
            ...itemToEdit,
        })

        setIsPipeOpeningFormModalActive(true)
    }

    const onChangeInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {

        setPipeOpeningConfigFormState({
            ...pipeOpeningConfigFormState,
            [e.target.name]: !isNaN(parseInt(e.target.value))  ? parseInt(e.target.value) : e.target.value
        })
    }

    const validateForm = (): boolean => {

        const mustNotBeEmptyError: string = `Must not be empty`
        const mustBeGreaterThanZero: string = `Must be greater than zero`

        const formErrors: FormErrorState = Object.entries(pipeOpeningConfigFormState).reduce((acc, [key, value]) => {
            
            return {
                ...acc,
                [key]: {
                    isError: value === null || value === '' || (!isNaN(parseInt(value.toString())) && parseInt(value.toString()) <= 0),
                    errorMessage: value === null || value === '' ? mustNotBeEmptyError : (!isNaN(parseInt(value.toString())) && parseInt(value.toString()) <= 0)  ? mustBeGreaterThanZero : null,
                }
            }
        }, initialFormErrorState)

        setFormErrorState(formErrors) 

        return Object.entries(formErrors).every(([key, value])=> {
            const formError: FormInputError = value
            return formError.isError === false
        })
    }

    const onSubmitFormEdit = async (e: React.MouseEvent) => {
        e.preventDefault()

        const isFormValid: boolean = validateForm()

        if (isFormValid) {

            const editedPipeOpening: PipeOpeningConfig = {
                id: itemToEdit.id,
                isDeleted: false,
                ...pipeOpeningConfigFormState
            }

            try {
                await editPipeOpening(editedPipeOpening)
                
                toastSuccess(`Success, pipe opening updated.`)

                onCancelModal(e)
                setIsConfirmEditModalActive(false)
                
            } catch (error) {
                toastError()
            }
        }
    }


    const onSubmitFormAdd = async (e: React.MouseEvent) => {
        e.preventDefault()

        const isFormValid: boolean = validateForm();

        if (isFormValid) {

            const newPipeOpening: AddPipeOpeningConfigRequest = pipeOpeningConfigFormState 

            try {
                await addPipeOpening(newPipeOpening)
                
                toastSuccess(`Success, pipe opening added.`)
                onCancelModal(e)
                
            } catch (error) {
                toastError()
            }
        }
    }

    const deletePipeOpeningItem = async (e: React.MouseEvent) => {
        e.preventDefault()

        try {
            await deletePipeOpening(itemToDelete)
            
            toastSuccess(`Success, pipe opening deleted.`)
            onCancelConfirmDeleteModal()
            
        } catch (error) {
            toastError()
        }
    }
     
    return(
        <div>
            <BaseModal
                isModalActive={isPipeOpeningFormModalActive}
                modalTitle={`${isInEditState ? `Edit `: `Add New `} Pipe Opening`}
                onCancelModal={onCancelModal}>
                    <form>
                        <div className="field">
                            <label className="label">Material</label> 
                            <div className={`select ${formErrorState.material && formErrorState.material.isError ? `is-danger`: ''}`}>
                                <select name="material" value={pipeOpeningConfigFormState.material} onChange={onChangeInput}>
                                    <option value="" disabled={true} >Select a material</option>
                                    {/* TODO - clear this up once we handle deletes properly */}
                                    {pipeMaterials && pipeMaterials.filter(_ => !_.isDeleted).map((pm, idx) => 
                                        <option key={`pipe-opening-material-${idx}`} value={pm.value}>{pm.label}</option>
                                    )}
                                </select>
                            </div>
                            {formErrorState.material && formErrorState.material.isError &&
                                <span className="help is-danger">{formErrorState.material.errorMessage}</span>
                            }
                        </div>

                        <InputField
                            fieldLabel={`Inside Diameter`}
                            onChange={onChangeInput}
                            fieldName={'insideDiameter'}
                            value={pipeOpeningConfigFormState.insideDiameter}
                            errorValue={formErrorState.insideDiameter}
                            helpText={``}
                            isNumber={true}
                            hasAddOn={`mm`}
                            isExpanded={true} />     
                                                                    
                        <InputField
                            fieldLabel={`Opening Size`}
                            onChange={onChangeInput}
                            fieldName={'openingSize'}
                            value={pipeOpeningConfigFormState.openingSize}
                            errorValue={formErrorState.openingSize}
                            helpText={``}
                            isNumber={true}
                            hasAddOn={`mm`}
                            isExpanded={true} />  

                        <InputField
                            fieldLabel={`Outside Diameter`}
                            onChange={onChangeInput}
                            fieldName={'outsideDiameter'}
                            value={pipeOpeningConfigFormState.outsideDiameter}
                            errorValue={formErrorState.outsideDiameter}
                            helpText={``}
                            isNumber={true}
                            hasAddOn={`mm`}
                            isExpanded={true} />                                         
                                    

                        <div className="field is-grouped is-pulled-right pt-4">
                            <div className="control">
                                <button className={`button is-link ${(isAddingPipeOpening || isEditingPipeOpening) ? `is-loading` : ``}`} onClick={isInEditState  ? (e) => { e.preventDefault(); setIsConfirmEditModalActive(true) } : onSubmitFormAdd}>{isInEditState ? `Edit` : `Add`}</button>
                            </div>
                            <div className="control">
                                <button className="button is-link is-light" onClick={onCancelModal}>Cancel</button>
                            </div>
                        </div>

                    </form>
            </BaseModal>

        <ConfirmActionModal 
            isModalActive={isConfirmDeleteModalActive}
            isLoading={isDeletingPipeOpening}
            title={`Are you sure you wish to continue?`}
            subtitle={`By deleting this pipe opening you will delete it for all products which currently use it.`}
            Icon={<FontAwesomeIcon icon={faWarning} size={`3x`} />}
            confirmBtnText={`Continue`}
            onConfirmAction={deletePipeOpeningItem} 
            onCancel={onCancelConfirmDeleteModal} />

        <ConfirmActionModal 
            isModalActive={isConfirmEditModalActive}
            isLoading={isEditingPipeOpening}
            title={`Are you sure you wish to continue?`}
            subtitle={`By editing this pipe opening you will update it for all products which currently use it.`}
            Icon={<FontAwesomeIcon icon={faWarning} size={`3x`} />}
            confirmBtnText={`Continue`}
            onConfirmAction={onSubmitFormEdit} 
            onCancel={() => setIsConfirmEditModalActive(false)} />

        <div className="columns mb-6">
            <div className="column is-10">
                    <h1 className="title is-3">Manage Pipe Openings</h1>
                    <h1 className="subtitle is-5 mb-3"></h1>
            </div>
            <div className="column">
                <button className="button is-link is-pulled-right" onClick={() => setIsPipeOpeningFormModalActive(true)}>Add <FontAwesomeIcon className="pl-2" icon={faPlus} /></button>
            </div>
        </div>

        {pipeMaterials && pipeOpenings && pipeOpenings.length > 0 
        ?
        <>
        <div className="columns mb-4 pb-4">
            <div className="column is-5 p-0">
                <SearchBar 
                    onChange={onChangeSearchQuery}
                    onClear={onClearSearchQuery} />
            </div>
        </div>

        {filterablePipeOpenings && filterablePipeOpenings.length > 0
        ?
        <div className="columns">
            <div className="column is-12">
                <div className="block">

                {/* TODO: Can be pulled out into a common table header component */}
                <div className="columns is-vcentered" style={{ borderBottom: '1px solid rgb(228, 228, 228)', backgroundColor: '#fafafa' }}>
                    <div className="column is-5">
                        <ColumnLabel label={`Material`} />
                    </div>
                    <div className="column is-2">
                        <ColumnLabel label={`Inside Diameter`} />
                    </div>
                    <div className="column is-2">
                        <ColumnLabel label={`Opening Size`} />
                    </div>
                    <div className="column is-2">
                        <ColumnLabel label={`Outside Diameter`} />
                    </div>
                    <div className="column is-1">
                        {/* Leave space for actions button. */}
                    </div>
                </div>

                    {filterablePipeOpenings
                    .sort((a, b) => {
                        if (a.material.toUpperCase() > b.material.toUpperCase()) return 1;
                        if (a.material.toUpperCase() < b.material.toUpperCase()) return -1;
                        return 0;
                    })
                    .map((item, idx) => (
                        <div className={`columns is-vcentered pb-1 pt-1 ${item.isDeleted ? `has-background-white-bis` : ``}`} key={`code-${idx}`} style={{borderBottom: '#e4e4e4 1px solid'}}>
                            <div className="column is-5">
                                {pipeMaterials.find(_ => _.value === item.material).label}
                            </div>   
                            <div className="column is-2">
                                {`${item.insideDiameter}mm`}
                            </div> 
                            <div className="column is-2">
                                {`${item.openingSize}mm`}
                            </div>
                            <div className="column is-2">
                                {`${item.outsideDiameter}mm`}
                            </div>
                            {item.isDeleted
                                    ?
                                    <span className="tag is-danger is-light">Deleted</span>
                                    :
                                    <div className="column is-1">
                                    <div className="dropdown is-hoverable is-pulled-right">
                                        <div className="dropdown-trigger pl-3 pr-3 is-clickable">
                                            <FontAwesomeIcon aria-haspopup="true" aria-controls="dropdown-menu4" icon={faEllipsisVertical} size={`lg`} />
                                        </div>
                                        <div className="dropdown-menu" id="dropdown-menu4" role="menu">
                                            <div className="dropdown-content">
                                                <a href="#" className="dropdown-item" onClick={(e) => onDeletePipeOpeningItem(e, item)}>
                                                    <span><FontAwesomeIcon icon={faTrash} className="pr-2" /> Delete</span>
                                                </a>
                                                <hr className="navbar-divider"/>
                                                <a href="#" className="dropdown-item" onClick={(e) => onEditPipeOpeningItem(e, item)}>
                                                    <span><FontAwesomeIcon icon={faPencil} className="pr-2" /> Edit</span>
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            }        
                        </div>
                    ))}
                    
                </div>
            </div>
        </div>
        :
        <div className="has-text-centered">
            <p>No pipe opening(s) found.</p>
        </div>
        }
        </>
        :
        <PageLoadSpinner />
    }         
    </div>
    )
}

export { ManagePipeOpeningsScreen }